import { useCallback, useEffect, useRef, useState } from 'react';
import { Menu } from '@/utils/webview-context-menu';
import {
  preparePreviewAsset,
  previewAttachmentSource,
} from '../../messages/file-reference-actions';
import { useT } from '@/utils/i18n';
import {
  diagnosePreviewFailure,
  previewFailureCopy,
  type AttachmentPreviewFailure,
} from '../../messages/attachment-preview-diagnostics';

/**
 * Custom video component with error handling.
 */
export const CueStreamdownVideo = ({
  src,
  sourcePath,
  environmentId,
  sourceSessionId,
  fileName,
  mimeType,
  onUnavailable,
  sizeBytes,
  className,
  ...props
}: React.ComponentProps<'video'> & {
  sourcePath?: string;
  environmentId?: string;
  sourceSessionId?: string;
  fileName?: string;
  mimeType?: string;
  onUnavailable?: (failure: AttachmentPreviewFailure) => void;
  sizeBytes?: number;
}) => {
  const t = useT();
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const prevSrcRef = useRef(src);

  // Clear error when src changes so a refreshed URL gets a clean start.
  useEffect(() => {
    if (prevSrcRef.current !== src) {
      prevSrcRef.current = src;
      setError(null);
    }
  }, [src]);

  const handleRetry = useCallback(() => {
    setError(null);
    setAttempt(n => n + 1);
  }, []);

  const handleLoadedData = useCallback(() => {
    if (typeof src === 'string') {
      preparePreviewAsset(src, fileName, mimeType, sizeBytes);
    }
  }, [fileName, mimeType, sizeBytes, src]);

  const handleLoadError = useCallback(() => {
    const source = typeof src === 'string' ? src : null;
    const copy = previewFailureCopy('video', {}, { fileName, mimeType });
    setError(copy.description);
    if (!source) {
      onUnavailable?.({
        description: copy.description,
        label: copy.label,
        source: null,
      });
      return;
    }

    void diagnosePreviewFailure('video', source, { fileName, mimeType })
      .then(failure => {
        setError(failure.description ?? failure.label);
        onUnavailable?.(failure);
      })
      .catch(() => {
        onUnavailable?.({
          description: copy.description,
          label: copy.label,
          source,
        });
      });
  }, [fileName, mimeType, onUnavailable, src]);

  const handleContextMenu = useCallback(
    (event: React.MouseEvent<HTMLVideoElement>) => {
      const previewSource = typeof src === 'string' ? src : undefined;
      if (!previewSource && !sourcePath) {
        return;
      }

      const menu = Menu.create().pushItem({
        title: t('Preview'),
        icon: Menu.icon.symbol('document.viewfinder'),
        onClick: () => {
          void previewAttachmentSource(previewSource, {
            fileName,
            mimeType,
            sizeBytes,
            fallbackPath: sourcePath,
            environmentId,
            sourceSessionId,
          });
        },
      });

      menu.popup(event);
    },
    [
      environmentId,
      fileName,
      mimeType,
      sizeBytes,
      sourcePath,
      sourceSessionId,
      src,
      t,
    ]
  );

  // Error state
  if (error) {
    return (
      <div className="inline-flex flex-col items-start bg-red-50 dark:bg-red-900/20 rounded p-4 border border-red-200 dark:border-red-800 max-w-full overflow-hidden gap-2">
        <span className="text-sm text-red-600 dark:text-red-400">{error}</span>
        <code className="text-xs text-red-500 dark:text-red-400 break-all">
          {src}
        </code>
        <button
          type="button"
          onClick={handleRetry}
          className="text-xs text-red-600 dark:text-red-400 underline hover:no-underline"
        >
          {t('Retry')}
        </button>
      </div>
    );
  }

  return (
    <video
      key={attempt}
      src={src}
      className={`w-full max-w-2xl rounded ${className || ''}`}
      controls
      onLoadedData={handleLoadedData}
      onContextMenu={handleContextMenu}
      onError={handleLoadError}
      {...props}
    />
  );
};
